import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  ChevronLeft,
  Power,
  Settings,
  Link2,
  Users,
  TrendingUp,
  Clock,
  Loader2,
  Bot as BotIcon,
  MessageSquare,
  Trash2,
  ListChecks
} from 'lucide-react'
import { useBot, useToggleBot, useDeleteBot } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useUser } from '@/shared/hooks/useUser'
import { useBackButton } from '@/shared/hooks/useBackButton'
import Toast from '@/components/ui/Toast'
import PullToRefresh from '@/components/ui/PullToRefresh'

export default function BotDetails() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { hapticFeedback, hapticNotification, webApp, telegramId } = useTelegram()
  const { data: user } = useUser(telegramId)
  const { data: bot, isLoading, refetch } = useBot(botId)
  const toggleBot = useToggleBot()
  const deleteBot = useDeleteBot()
  const [imageLoading, setImageLoading] = useState(true)
  const [imageError, setImageError] = useState(false)
  const [toastMessage, setToastMessage] = useState<string>('')
  const [showToast, setShowToast] = useState(false)

  // Auto-manage back button
  useBackButton()

  const handleRefresh = async () => {
    try {
      await refetch()
      hapticNotification?.('success')
    } catch (error) {
      console.error('Failed to refresh bot data:', error)
      hapticNotification?.('error')
    }
  }

  // Show toast if navigation state contains success message
  useEffect(() => {
    const state = location.state as { successMessage?: string } | null
    if (state?.successMessage) {
      setToastMessage(state.successMessage)
      setShowToast(true)
      // Clear navigation state after a small delay to allow toast to render
      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} })
      }, 100)
    }
  }, [location.state, navigate, location.pathname])

  const handleToggleBot = async () => {
    if (!bot) return

    hapticFeedback('medium')
    try {
      await toggleBot.mutateAsync(bot.id)
    } catch (error) {
      console.error('Failed to toggle bot:', error)
      hapticNotification('error')
    }
  }

  const handleAutoResources = () => {
    if (!bot) return
    hapticFeedback('light')
    window.scrollTo(0, 0)
    navigate(`/bots/${bot.id}/auto-resources`)
  }

  const handleTimeout = () => {
    if (!bot) return
    hapticFeedback('light')
    window.scrollTo(0, 0)
    navigate(`/bots/${bot.id}/timeout`)
  }

  const handleStatistics = () => {
    hapticFeedback('light')
    window.scrollTo(0, 0)
    navigate(`/bots/${botId}/statistics`)
  }

  const handleIntegration = () => {
    hapticFeedback('light')
    window.scrollTo(0, 0)
    navigate(`/bots/${botId}/integration`)
  }

  const handleCustomBlock = () => {
    hapticFeedback('light')
    window.scrollTo(0, 0)
    navigate(`/bots/${botId}/custom-block`)
  }

  const handleAccessManagement = () => {
    hapticFeedback('light')
    window.scrollTo(0, 0)
    navigate(`/bots/${botId}/access`)
  }

  const handleWhitelist = () => {
    hapticFeedback('light')
    window.scrollTo(0, 0)
    navigate(`/bots/${botId}/whitelist`)
  }

  const handleDeleteBot = () => {
    if (!bot) return

    webApp?.showPopup({
      title: 'Удалить бота',
      message: `Вы уверены, что хотите удалить бота @${bot.username}? Это действие нельзя отменить.`,
      buttons: [
        { id: 'cancel', type: 'cancel' },
        { id: 'delete', type: 'destructive', text: 'Удалить' },
      ],
    }, async (buttonId) => {
      if (buttonId === 'delete') {
        hapticFeedback('heavy')
        try {
          await deleteBot.mutateAsync(bot.id)
          webApp?.showPopup({
            title: 'Успешно',
            message: 'Бот успешно удален',
            buttons: [{ type: 'ok' }],
          }, () => {
            navigate('/bots', { replace: true })
          })
        } catch (error) {
          console.error('Failed to delete bot:', error)
          hapticNotification('error')
          webApp?.showPopup({
            title: 'Ошибка',
            message: 'Не удалось удалить бота. Попробуйте позже.',
            buttons: [{ type: 'ok' }],
          })
        }
      }
    })
  }

  if (isLoading || !bot) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  const isActive = bot.status === 'active' && bot.enabled

  const getLogoUrl = () => {
    if (!bot.id) return null

    const baseUrl = import.meta.env.VITE_OPSERVICE_BASE_URL || 'http://localhost:8080'
    return `${baseUrl}/api/v1/bots/${bot.id}/logo`
  }

  const logoUrl = getLogoUrl()

  return (
    <div className="min-h-screen bg-tg-bg pb-32">
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="pt-[96px]">
          {/* Header */}
          <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
          <div className="flex items-center gap-4">
            <div className={`relative w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center ${
              isActive
                ? 'bg-tg-button shadow-lg shadow-tg-button/25'
                : 'bg-tg-bg'
            }`}>
              {logoUrl && !imageError ? (
                <>
                  <img
                    src={logoUrl}
                    alt={`${bot.name} logo`}
                    className={`w-full h-full object-cover transition-opacity duration-200 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                    onError={() => {
                      setImageError(true)
                      setImageLoading(false)
                    }}
                    onLoad={() => {
                      setImageLoading(false)
                    }}
                  />
                  {imageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className={`w-6 h-6 animate-spin ${isActive ? 'text-tg-button-text' : 'text-[#6d7885]'}`} />
                    </div>
                  )}
                </>
              ) : (
                <BotIcon className={`w-8 h-8 ${isActive ? 'text-tg-button-text' : 'text-[#6d7885]'}`} />
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-tg-text">@{bot.username}</h1>
              <p className="text-sm text-tg-hint">{bot.name}</p>
            </div>
          </div>

          {/* Status badge on new line */}
          {(() => {
            // Determine bot status based on status and enabled fields
            let status: 'active' | 'inactive' | 'suspended' | 'pending_verification'
            if (bot.status === 'active' && bot.enabled) {
              status = 'active'
            } else if (bot.status === 'suspended') {
              status = 'suspended'
            } else if (bot.status === 'pending_verification') {
              status = 'pending_verification'
            } else {
              status = 'inactive'
            }

            const statusStyles = {
              active: {
                label: 'Активен',
                bgColor: 'bg-green-500/10',
                textColor: 'text-green-600',
                borderColor: 'border-green-500/30',
                dotColor: 'bg-green-500',
              },
              inactive: {
                label: 'Неактивен',
                bgColor: 'bg-gray-500/10',
                textColor: 'text-gray-600',
                borderColor: 'border-gray-500/30',
                dotColor: 'bg-gray-500',
              },
              pending_verification: {
                label: 'Ожидает проверки',
                bgColor: 'bg-yellow-500/10',
                textColor: 'text-yellow-600',
                borderColor: 'border-yellow-500/30',
                dotColor: 'bg-yellow-500',
              },
              suspended: {
                label: 'Приостановлен',
                bgColor: 'bg-red-500/10',
                textColor: 'text-red-500',
                borderColor: 'border-red-500/30',
                dotColor: 'bg-red-500',
              },
            }

            const style = statusStyles[status]

            return (
              <div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${style.bgColor} ${style.textColor} border ${style.borderColor}`}>
                  <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${style.dotColor}`} />
                  {style.label}
                </span>
              </div>
            )
          })()}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Quick Settings */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Быстрые настройки</h2>
          </div>

          <button
            onClick={handleAutoResources}
            className="w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors border-b border-tg-secondary-bg"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
                <Settings className="w-5 h-5 text-orange-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-tg-text">Авто-ресурсы</div>
                <div className="text-xs text-tg-hint">Фильтры и ценообразование</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
          </button>

          <button
            onClick={handleWhitelist}
            className="w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors border-b border-tg-secondary-bg"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <ListChecks className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-tg-text">Мои ресурсы</div>
                <div className="text-xs text-tg-hint">Выбор своих ресурсов для бота</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
          </button>

          <button
            onClick={handleTimeout}
            className="w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors border-b border-tg-secondary-bg"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#5288c1]/20 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#5288c1]" />
              </div>
              <div className="text-left">
                <div className="font-medium text-tg-text">Таймаут</div>
                <div className="text-xs text-tg-hint">{Math.round(bot.timeout / 3600)} часов</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
          </button>

          <button
            onClick={handleCustomBlock}
            className="w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors border-b border-tg-secondary-bg"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-tg-text">Настройка блока</div>
                <div className="text-xs text-tg-hint">Кастомизация сообщений</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
          </button>

          <button
            onClick={handleStatistics}
            className="w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#059669]/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#059669]" />
              </div>
              <div className="text-left">
                <div className="font-medium text-tg-text">Статистика</div>
                <div className="text-xs text-tg-hint">Подробная аналитика</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
          </button>
        </div>

        {/* Advanced Settings */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Дополнительно</h2>
          </div>

          <button
            onClick={handleIntegration}
            className="w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors border-b border-tg-secondary-bg"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                <Link2 className="w-5 h-5 text-indigo-400" />
              </div>
              <span className="font-medium text-tg-text">Интеграция</span>
            </div>
            <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
          </button>

          <button
            onClick={handleAccessManagement}
            className="w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#5288c1]/20 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-[#5288c1]" />
              </div>
              <span className="font-medium text-tg-text">Управление доступом</span>
            </div>
            <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
          </button>
        </div>

        {/* Toggle Status */}
        <button
          onClick={handleToggleBot}
          disabled={toggleBot.isPending}
          className={`w-full p-5 rounded-2xl font-semibold text-lg shadow-lg active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            isActive
              ? 'bg-red-600 text-white shadow-red-600/25'
              : 'bg-[#059669] text-white shadow-[#059669]/25'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Power className="w-6 h-6" />
            {toggleBot.isPending
              ? 'Обработка...'
              : isActive
              ? 'Остановить бота'
              : 'Запустить бота'}
          </div>
        </button>

        {/* Delete Bot (Owner Only) */}
        {user && bot.owner_id === user.id && (
          <button
            onClick={handleDeleteBot}
            disabled={deleteBot.isPending}
            className="w-full p-4 rounded-2xl font-medium bg-red-500/10 text-red-600 border-2 border-red-500/30 hover:bg-red-500/20 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-4"
          >
            <div className="flex items-center justify-center gap-2">
              <Trash2 className="w-5 h-5" />
              {deleteBot.isPending ? 'Удаление...' : 'Удалить бота'}
            </div>
          </button>
        )}
      </div>
        </div>
      </PullToRefresh>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type="success"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  )
}
